'use client'
import { FC, useState } from 'react'
import { FundCard } from '@domain/funds/components/fund-card/FundCard'
import { FundStage } from '@domain/funds/model'
import clsx from 'clsx'
import { CardHeader } from '@components/ui/card/CardHeader'
import { cn } from '@utils/tailwind.utils'
import { InfoLightIcon } from '@components/icons/light'
import { Tooltip } from '@components/ui/tooltip'
import { useRouter } from 'next/navigation'
import { EquityAuditDialog } from '@domain/pre-ipo-access/components/EquityAuditDialog'
import { PreIpoStageTag } from '@domain/pre-ipo-access/components/PreIpoStageTag'

export interface FundCardWithSectionsProps {
  disabled?: boolean
  variant?: 'light' | 'dark'
  detailsPath: string
  proofLink?: string | null
  imgIcon: string
  content?: React.ReactNode
  footerButtons: React.ReactNode
  description: string
  stage?: FundStage
  title: string
  contentWrapperClassName?: string
  badges?: string[]
}

const renderBadge = (badge: string, index: number, variant: 'light' | 'dark') => {
  const images = {
    light: [
      '/images/wlth/BadgeIcon.webp',
      '/images/wlth/ShieldIcon.webp',
      '/images/wlth/TriangleIcon.webp',
    ],
    dark: [
      '/images/wlth/BadgeIconWht.webp',
      '/images/wlth/ShieldIconWht.webp',
      '/images/wlth/TriangleIconWht.webp',
    ],
  }
  return (
    <li key={index} className="text-sm flex gap-2 items-center">
      <img
        src={images[variant][index]}
        className="w-4 h-4"
        width={16}
        height={16}
        alt="Badge"
        loading="lazy"
        sizes="16px"
      />
      <span
        className={clsx('font-montserrat font-medium text-sm', {
          'text-white': variant === 'dark',
        })}
      >
        {badge}
      </span>
    </li>
  )
}

export const FundCardWithSections: FC<FundCardWithSectionsProps> = ({
  disabled = false,
  variant = 'light',
  detailsPath,
  proofLink,
  imgIcon,
  content,
  footerButtons,
  description,
  stage,
  title,
  contentWrapperClassName,
  badges,
}) => {
  const router = useRouter()
  const [auditOpen, setAuditOpen] = useState(false)

  const handleProofLinkClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
    if (proofLink) {
      setAuditOpen(true)
    }
  }

  const renderFundStatus = (status: FundStage | undefined) => {
    if (!status) return null

    return (
      <div className="flex items-center justify-end gap-5 p-4">
        <PreIpoStageTag stage={status} size="lg" />
      </div>
    )
  }

  const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent navigation if clicking on the proof link button or its container
    const target = e.target as HTMLElement
    if (target.closest('[data-proof-link]')) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (disabled) return
    router.push(detailsPath)
  }

  return (
    <div
      className={cn('w-full h-full select-text cursor-pointer', {
        'pointer-events-none cursor-default': disabled,
      })}
      onClick={handleLinkClick}
      role="link"
      aria-label={title}
    >
      <FundCard
        contentClassName="flex flex-col gap-6 select-text"
        className={clsx(
          'h-full outline-3 outline-cw-purple transition-all hover:outline min-h-[27.25rem] bg-white select-text',
          {
            '!border-none bg-cw-purple-dark-medium': variant === 'dark',
          },
        )}
        headerChildren={renderFundStatus(stage)}
      >
        <CardHeader
          imgSrc={imgIcon}
          name={title}
          description={description}
          variant={variant}
        />
        <div
          className={`flex justify-between select-text gap-2 flex-col sm:flex-row ${contentWrapperClassName}`}
        >
          {content}
        </div>
        {badges && badges.length > 0 && (
          <div className="mt-auto">
            <ul className="flex flex-col gap-2">
              {badges?.map((badge, index) => renderBadge(badge, index, variant))}
            </ul>
          </div>
        )}
        {proofLink && (
          <div
            className="w-[160px]"
            data-proof-link
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              e.nativeEvent.stopImmediatePropagation()
            }}
          >
            <button
              type="button"
              className={`w-auto cursor-pointer ${
                variant === 'dark' ? 'text-white' : 'text-cw-midnight-purple'
              } hover:text-cw-electric-purple transition-colors`}
              onClick={handleProofLinkClick}
            >
              <Tooltip className="static" content="Proof Of Ownership">
                <InfoLightIcon className="w-6 h-6 mr-1" />
              </Tooltip>
              Equity-backed
            </button>
          </div>
        )}
        <EquityAuditDialog
          open={auditOpen}
          onOpenChange={setAuditOpen}
          auditUrl={proofLink}
        />
        <div className="relative flex flex-col sm:flex-row gap-4 sm:pt-16 mt-auto">
          {footerButtons}
        </div>
      </FundCard>
    </div>
  )
}
